// 前端异常现场采集
//
// 所有走 fetch 的接口失败（HTTP 非 2xx / 业务 code !== '200'）都会经过
// errorHandler，这里挂一个 ring buffer 采集现场，方便事后定位。
//
// 访问方式：
//   localStorage.getItem('erpify:error-log')  —— 最近 20 条 JSON 数组
//   window.__ERROR_LOG__.getAll()             —— 同上
//   window.__ERROR_LOG__.last()               —— 最新一条
//   window.__ERROR_LOG__.copy()               —— 一键 copy 全部到剪贴板
//   window.__ERROR_LOG__.clear()              —— 清空

const STORAGE_KEY = 'erpify:error-log'
const MAX_ENTRIES = 20
const SENSITIVE_HEADER_KEYS = ['X-Token', 'x-token', 'Authorization', 'authorization']
const SENSITIVE_BODY_KEYS = ['password', 'passwd', 'pwd', 'token', 'captcha', 'idCard', 'idcard']

export interface ErrorDumpEntry {
  ts: string
  currentUrl: string
  userId?: number | string
  method?: string
  url?: string
  baseURL?: string
  httpStatus?: number
  bizCode?: string | number
  bizMsg?: string
  reqHeaders?: Record<string, any>
  reqBody?: any
  respBody?: any
  stack?: string
  message?: string
  triggeredLogout?: boolean
}

function nowString(): string {
  const d = new Date()
  const pad = (n: number, w = 2) => String(n).padStart(w, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`
}

function maskSensitiveString(v: string): string {
  if (typeof v !== 'string' || v.length <= 4) return v
  return `${v.slice(0, 4)}****（共 ${v.length} 位）`
}

function maskHeaders(h: any): Record<string, any> {
  if (!h || typeof h !== 'object') return {}
  const out: Record<string, any> = {}
  Object.keys(h).forEach(k => {
    const v = h[k]
    if (SENSITIVE_HEADER_KEYS.includes(k) && typeof v === 'string') {
      out[k] = maskSensitiveString(v)
    } else {
      out[k] = v
    }
  })
  return out
}

function maskBody(body: any): any {
  if (body == null) return body
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body)
      return maskBody(parsed)
    } catch {
      return body
    }
  }
  if (Array.isArray(body)) return body.map(maskBody)
  if (typeof body === 'object') {
    const out: Record<string, any> = {}
    Object.keys(body).forEach(k => {
      if (SENSITIVE_BODY_KEYS.includes(k.toLowerCase())) {
        out[k] = '***'
      } else {
        out[k] = maskBody(body[k])
      }
    })
    return out
  }
  return body
}

function readStorage(): ErrorDumpEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}

function writeStorage(arr: ErrorDumpEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr))
  } catch {
    // 超限就清老的
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(-5)))
    } catch {
      // ignore
    }
  }
}

function push(entry: ErrorDumpEntry): void {
  const arr = readStorage()
  arr.push(entry)
  while (arr.length > MAX_ENTRIES) arr.shift()
  writeStorage(arr)
}

// 采集入口：errorHandler 报错路径里调用
// e 可以是 axios error（HTTP 非 2xx）或业务 code!==200 场景下的 response.data 对象
export function dumpError(e: any, context: { triggeredLogout?: boolean } = {}): void {
  const entry: ErrorDumpEntry = {
    ts: nowString(),
    currentUrl: typeof location !== 'undefined' ? (location.pathname + location.search) : '',
    triggeredLogout: !!context.triggeredLogout,
  }

  // 当前用户
  try {
    const w: any = typeof window !== 'undefined' ? window : {}
    if (w.__STORE__ && typeof w.__STORE__.getState === 'function') {
      entry.userId = w.__STORE__.getState()?.user?.userInfo?.userId
    } else {
      // 兜底从 cookie 拿
      const m = document.cookie.match(/X-UID=([^;]+)/)
      if (m) entry.userId = decodeURIComponent(m[1])
    }
  } catch {}

  // axios error（走 response interceptor 的 error 分支）
  // 业务 code !== 200 分支 e 是 response.data + 注入 __config（见 services/index.ts）
  const axiosConfig = e?.config || e?.__config
  const axiosResponse = e?.response
  if (axiosConfig) {
    entry.method = (axiosConfig.method || '').toUpperCase()
    entry.url = axiosConfig.url
    entry.baseURL = axiosConfig.baseURL
    entry.reqHeaders = maskHeaders(axiosConfig.headers)
    entry.reqBody = maskBody(axiosConfig.data)
  }
  if (axiosResponse) {
    entry.httpStatus = axiosResponse.status
    entry.respBody = axiosResponse.data
    entry.bizCode = axiosResponse.data?.code
    entry.bizMsg = axiosResponse.data?.msg
  }

  // 业务 code !== 200（走 response interceptor 的 success 分支）e 就是 response.data
  // （从 services/index.ts 注入 __config 后已走 axiosConfig 分支上面，这里是兜底）
  if (!axiosConfig && !axiosResponse) {
    entry.bizCode = e?.code
    entry.bizMsg = e?.msg
    entry.respBody = e
    if (e?.message) entry.message = e.message
  } else if (axiosConfig && !axiosResponse) {
    // success-branch 走到 axiosConfig 分支（因为注入了 __config）
    // 此时 e 仍是 response.data，需要把 bizCode/bizMsg/respBody 也填上
    entry.bizCode = entry.bizCode || e?.code
    entry.bizMsg = entry.bizMsg || e?.msg
    entry.respBody = entry.respBody || e
  }

  // stack
  if (e?.stack) {
    entry.stack = String(e.stack).split('\n').slice(0, 10).join('\n')
  } else {
    entry.stack = new Error('dumpError-callsite').stack?.split('\n').slice(2, 8).join('\n')
  }

  push(entry)

  // 开发 / 真实调试都打在控制台里，折叠不占屏
  try {
    // eslint-disable-next-line no-console
    console.groupCollapsed(`%c[errorDump] ${entry.ts} ${entry.method || ''} ${entry.url || entry.bizCode || ''}`,
      'color: #ef4444; font-weight: bold')
    // eslint-disable-next-line no-console
    console.log(entry)
    // eslint-disable-next-line no-console
    console.log('查看全部：localStorage.getItem(\'erpify:error-log\') 或 window.__ERROR_LOG__.getAll()')
    // eslint-disable-next-line no-console
    console.groupEnd()
  } catch {}
}

// 注册全局工具
export function installErrorDumpGlobal(): void {
  const w: any = typeof window !== 'undefined' ? window : {}
  w.__ERROR_LOG__ = {
    getAll: readStorage,
    last: () => {
      const arr = readStorage()
      return arr[arr.length - 1]
    },
    clear: () => {
      try { localStorage.removeItem(STORAGE_KEY) } catch {}
    },
    copy: async () => {
      const arr = readStorage()
      const text = JSON.stringify(arr, null, 2)
      try {
        await navigator.clipboard.writeText(text)
        // eslint-disable-next-line no-console
        console.info(`[errorDump] 已复制 ${arr.length} 条到剪贴板`)
      } catch {
        // eslint-disable-next-line no-console
        console.info(text)
      }
    },
    _dump: dumpError,
  }
}
